/**
 * AdvicePrep assistant ("Preppy") — shared types.
 *
 * Five capabilities live behind one panel, all now on real endpoints:
 *   analysis  the deep-dive chat
 *   popups    behaviour-triggered messages
 *   guides    on-screen walkthroughs + Help Centre articles from Intercom
 *   support   raise a ticket, track status
 *   feedback  one rating and one box
 */

export type AssistantTabKey = 'analysis' | 'popups' | 'guides' | 'support' | 'feedback'

/** Preppy's expressions. Applied to every placement at once. */
export type PreppyMood = 'idle' | 'think' | 'happy' | 'worry' | 'point'

/* ── pop-ups ────────────────────────────────────────────────────────────── */

/**
 * The four stages of the AdvicePrep in-app notification model.
 *
 * Every trigger pops in the corner as it fires; the Pop-ups tab is the history
 * of what has already popped. Switching a stage off stops it popping entirely —
 * it does not merely go quiet.
 */
export type PopupTrigger = 'firsttime' | 'everyuse' | 'inactivity' | 'milestones'

export type PopupIcon =
  | 'bulb'
  | 'plug'
  | 'cross'
  | 'plus'
  | 'doc'
  | 'chart'
  | 'check'
  | 'gear'
  | 'bell'
  | 'clock'
  | 'star'

export const POPUP_EVENT_KEYS = [
  // firsttime — once per user, ever
  'signup',
  'xero_connected',
  'first_meeting_created',
  'first_agenda_created',
  'first_analysis_generated',
  'first_notes_added',
  'branding_updated',
  'avatar_setup',
  'first_export',
  'first_meeting_completed',
  'first_recommendation_edited',
  // everyuse
  'login_welcome',
  // Every connection after the first. A separate event rather than reusing
  // `xero_connected`, because the copy is a different thought: the first one
  // explains what happens next, this one is about the practice growing.
  'xero_connected_again',
  // Moved here from `firsttime`: a sync can fail on any connection at any time,
  // and telling someone only about the first failure they ever had is silence
  // on every one after it.
  'xero_sync_failed',
  'notes_added',
  'analysis_generated',
  'meeting_first_export',
  'meeting_tomorrow',
  // inactivity
  'inactive_7d',
  'inactive_30d',
  // milestones — counted against the firm, never the user. "Firm" is the
  // practice (clientId). See PREPPY-POPUPS-SPEC.md §1.1 — the practice and its
  // customer were conflated in earlier drafts, which is why these are not named
  // client_*.
  'firm_analysis_3',
  'firm_analysis_10',
  'firm_analysis_25',
  'firm_meetings_3',
  'teammate_first_analysis',
  'firm_second_meeting',
  'unknown',
] as const

export type PopupEventKey = (typeof POPUP_EVENT_KEYS)[number]

/**
 * What a pop-up points at. The server sends a kind and ids, never a URL, and
 * the destination is chosen from the `eventKey` rather than from `kind` — see
 * `popupTargetRoute` in ./popupBehaviour.ts, which reads only the ids.
 * `kind` is kept because the API sends it and it is useful when diagnosing a
 * target, not because anything routes on it.
 */
export type PopupTarget = {
  orgId?: string
  callId?: string
}

/** Mirrors the API DTO (spec §3.1). Icon and buttons are derived, not sent. */
export type PopupMessage = {
  id: string
  /** Which event this is. Drives the icon, the buttons, retention and routing. */
  eventKey: PopupEventKey
  trigger: PopupTrigger
  title: string
  body: string
  /** ISO 8601 with an explicit offset — normalised by the adapter, which has to
   *  correct for the API sending a bare UTC timestamp with no `Z`. */
  createdAt: string
  unread: boolean
  /** Already popped in the corner. Distinct from `unread`: you can watch one
   *  appear and ignore it, which leaves it seen-but-unread. */
  seen: boolean
  /**
   * Warmer copy for a significant moment. Tone only, drives no styling — and
   * today it matches the eight messages that open with an emoji (spec §12).
   */
  playful: boolean
  target: PopupTarget
}

/** The one event that renders the tour buttons instead of being dismissable. */
export const WELCOME_EVENT_KEY: PopupEventKey = 'signup'

export const isWelcomePopup = (popup: PopupMessage) => popup.eventKey === WELCOME_EVENT_KEY

export type PopupSetting = {
  key: PopupTrigger
  name: string
  /** How this stage is delivered, in plain words. */
  delivery: string
  /** Raw channels from the server, e.g. ['inApp', 'email']. */
  channels: string[]
  on: boolean
}

/* ── guides ─────────────────────────────────────────────────────────────── */

/**
 * One stop on a walkthrough.
 *
 * `anchor` resolves to `[data-tour="<anchor>"]` rather than a CSS id — ids in a
 * React tree are incidental and get renamed; the attribute is a deliberate
 * contract with the step components.
 *
 * `step` is declarative on purpose: the walkthrough asks the wizard to move to
 * that step before measuring, instead of the step carrying a callback closure.
 */
export type WalkStep = {
  anchor: string
  text: string
  id?: string
  choices?: WalkChoice[]
  onlyWhen?: WalkCondition
  /**
   * Route to be on before pointing.
   *
   * Supports `:orgId` and `:callId`, substituted from whatever the user
   * actually has open — or, failing that, the first organisation and meeting
   * available to them. A step whose placeholders cannot be filled is skipped
   * rather than shown pointing at nothing.
   */
  route?: string
  /** Zero-based wizard step to move to before pointing. Prep screens only. */
  step?: number
  /**
   * This step points at something that only exists once the analysis has run.
   *
   * Generating it takes minutes on the server, so a step like this is skipped
   * rather than waited on — there is no amount of patience that turns an
   * unanalysed report into findings, and nobody sits through a walkthrough that
   * has paused for several minutes.
   */
  needsAnalysis?: boolean
  /**
   * What moves the walkthrough on from this step.
   *
   * `click` is the default and is Preppy driving — it narrates, you press Next.
   * The others hand the wheel over: the step describes what to do, then waits
   * for you (or the server) to do it and advances by itself.
   *
   *   anchorClicked   the user clicked the element this step is pointing at.
   *                   The generic one: it needs nothing from the page, only a
   *                   `data-tour` that is already there to be spotlit
   *   wizardAdvanced  the user got the prep wizard past this step's own `step`,
   *                   which means the page's own validation passed — required
   *                   fields and all — without the walkthrough duplicating it
   *   findingsSaved   the user saved an edit to the findings — the save itself,
   *                   not the click on Save changes, which only opens the
   *                   confirmation dialog
   *   meetingCreated  a meeting now exists on this organisation, however it got
   *                   there. Not the click: the button posts and then navigates
   *   analysisReady   the report finished generating, minutes later
   */
  advanceWhen?: WalkAdvance
  /** What satisfies `anchorClicked`, when that is not the whole spotlit element. */
  clickAnchor?: string
  /** Next stays live while this step waits, so the wait is a shortcut not a gate. */
  waitOptional?: boolean
  /** Spotlight this instead whenever it is on screen; falls back to `anchor`. */
  focusAnchor?: string
  /** Highlight every element carrying the anchor. Requires `allowInteraction`. */
  highlightAll?: boolean
  /** Leave clicks outside the spotlight working. */
  allowInteraction?: boolean
  /** Id of a guide to hand over to instead of moving to the next step. */
  handOff?: string
  /** Shown while waiting. Falls back to `text` when absent. */
  waitText?: string
  /** Shown when the walkthrough is running against the sample prep. */
  sampleText?: string
  /** Overrides the "Next" label, for a step where moving on means declining. */
  nextLabel?: string
  /**
   * Shown when a dialog has opened and the spotlight has moved onto it.
   *
   * Worth its own line because the instruction genuinely changes: the step was
   * about the form, and now it is about the dialog in front of it.
   */
  modalText?: string
}

export type WalkAdvance =
  | 'click'
  | 'anchorClicked'
  | 'wizardAdvanced'
  | 'meetingCreated'
  | 'findingsSaved'
  | 'analysisReady'

export type WalkCondition = 'noMeeting'

/** One button on a branching step. */
export type WalkChoice = {
  label: string
  /** The `id` of the step this choice jumps to. */
  goTo: string
  /** Absent means the real path; inheriting would only ever turn the sample on. */
  sample?: boolean
  secondary?: boolean
}

/* ── help articles ──────────────────────────────────────────────────────── */

/**
 * One Help Centre article (PREPPY-HELP-SPEC.md).
 *
 * Shaped after Intercom's Article object; `category` and `minutes` are derived
 * by the proxy, since Intercom has neither in a usable form.
 */
export type HelpArticle = {
  /** Intercom's article id. Stable across title and body edits. */
  id: string
  title: string
  /** Intercom's own one-line summary. Shown under the title, not in the list. */
  description: string
  /** From the payload, never built: Intercom's URLs carry the id and locale. */
  url: string
  /** The Intercom Collection this sits in, resolved server-side. */
  category: string
  /** Sanitised HTML. Never rendered without passing through DOMPurify. */
  bodyHtml: string
  /** ISO 8601. Intercom sends Unix seconds; the adapter converts. */
  updatedAt: string
  /** Derived from the body's word count — Intercom has no reading time. */
  minutes: string
}

/** What `:orgId` / `:callId` resolve to for the running walkthrough. */
export type WalkParams = {
  orgId: string
  callId: string
}

/**
 * Which AdvicePrep screen you are on, and therefore which walkthroughs can run.
 *
 * A guide that points at the prep wizard has nothing to point at on the
 * organisation list, so it is filtered out there rather than offered and then
 * failing to find its anchors. Screens with no walkthrough of their own still
 * get Pop-ups, Support, Feedback and the whole Help Centre.
 */
export type GuideScope = 'org-list' | 'calls' | 'prep' | 'consolidated' | 'settings'

export type Guide = {
  id: string
  title: string
  body: string
  category: string
  minutes: string
  scopes: GuideScope[]
  /** Not listed at all until the open report has an analysis. */
  needsAnalysis?: boolean
  /** Asks the user to save something, so it cannot run against the sample prep. */
  needsRealCall?: boolean
  steps: WalkStep[]
}

/* ── suggestions ────────────────────────────────────────────────────────── */

/**
 * Something Preppy can offer to open for you — an organisation, a meeting.
 *
 * The analysis chat reads one call at a time, so on a screen with no call in
 * context the honest thing is not "go and pick one somewhere else" — it is to
 * show what is actually there and take you to whichever you choose.
 */
export type AssistantSuggestion = {
  /** Stable key, and the id of the thing being offered. */
  id: string
  title: string
  /** Second line: how far the prep has got, or when data last refreshed. */
  detail: string
  /** Optional status pill. `colour` is an antd Tag colour. */
  tag?: { label: string; colour: string }
  /** Route that clicking this navigates to. */
  to: string
}

export type AssistantSuggestions = {
  items: AssistantSuggestion[]
  loading: boolean
  /** What Preppy says above the list. */
  intro: string
  /** The quieter line underneath it. */
  note: string
}

/* ── support ────────────────────────────────────────────────────────────── */

/** The four wire values, and nothing else (PREPPY-SUPPORT-SPEC.md §3.2). The only ones the UI may set. */
export type TicketStatus = 'open' | 'reviewing' | 'awaiting' | 'closed'

/**
 * What a ticket read from the API can be in. A value the UI does not know stays
 * `unknown` rather than being coerced to one it does, so nothing acts on a guess.
 */
export type TicketStatusValue = TicketStatus | 'unknown'

export type SupportTicket = {
  /** The Guid. Correct for the API, and never shown to anyone. */
  id: string
  /** `DI-4821` — the human-quotable id, and what the UI displays. */
  reference: string
  subject: string
  area: string
  status: TicketStatusValue
  body: string
  /** The connection the ticket is about. Absent only on tickets raised before it was required. */
  platformId?: string
  raisedFrom?: string
  /** ISO with an offset. Formatted at the point of display, not here. */
  raisedAt: string
  updatedAt: string
}

/** What the admin console gets on top: who asked, and from which firm. */
export type AdminSupportTicket = SupportTicket & {
  raisedByName: string
  raisedByEmail: string
  clientId: string
  clientName: string
  platformName: string
}

/** One message in a ticket's thread, from either side. */
export type TicketReply = {
  id: string
  body: string
  fromSupport: boolean
  /**
   * Support answered out of band — from their own mail client — and logged it
   * here afterwards. The customer was NOT emailed a notice for this message,
   * because they already have the real one.
   */
  sentByEmail: boolean
  sentByName: string
  sentAt: string
}

/**
 * One row of the append-only status log.
 *
 * `fromStatus` is absent on the opening row rather than null, because the
 * ticket was not in any state before it was raised.
 */
export type TicketHistoryEntry = {
  fromStatus?: TicketStatusValue
  toStatus: TicketStatusValue
  /** Already a display name — the endpoint joins it, so no id lookup here. */
  changedByName: string
  changedAt: string
}

export type SupportDraft = {
  subject: string
  area: string
  body: string
  /**
   * `null` until the user picks anything, which is when the organisation on
   * screen is used instead. `''` is the None / General option — chosen, and
   * deliberately about no organisation, so it never falls back. Anything else is
   * a platform id.
   */
  platformId: string | null
}

export type TicketPlatformOption = {
  platformId: string
  companyId: string
  label: string
}

/* ── feedback ───────────────────────────────────────────────────────────── */

export type FeatureVerdict = 'up' | 'down'

export type RatableFeature = {
  key: string
  name: string
  hint: string
}

export type FeedbackDraft = {
  rating: number
  text: string
  contactOk: boolean
  verdicts: Record<string, FeatureVerdict | undefined>
  detailOpen: boolean
}

/* ── walkthrough runtime ────────────────────────────────────────────────── */

export type WalkthroughState = {
  guideId: string
  index: number
  /**
   * False when the step was reached by pressing Back — otherwise an
   * already-satisfied step bounces you straight forward again.
   */
  allowAuto: boolean
  /** `:callId` resolves to the sample prep for the rest of the walkthrough. */
  sample?: boolean
}
